import { api } from 'api';
import {
  ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME,
  ADD_TRANSACTION_RECEIVER_GET_NAME,
  ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  FUNC_DEFAULT,
} from 'constants';
import { checkMaintenance, checkSystemError } from 'helpers/maintenance';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useCommonStore from 'hooks/useCommonStore';
import { isEmpty } from 'lodash';
import { SET_ADD_TRANSACTION_INFORMATION } from 'store/action';
import { numberUtils } from 'utils';
import {
  buildQuoteSignature,
  isStoredQuoteReusable,
} from '../func/quoteLifecycle';
import useCalculatorRoundingAPI from './useCalculatorRoundingAPI';
import useCalculatorRoundingStore from './useCalculatorRoundingStore';

const useCalculatorRounding = () => {
  const { remitRoundingRequest } = useCalculatorRoundingAPI();
  const {
    calculatorRoundingResponse,
    setCalculatorRoundingResponse,
    isRateFetching,
    setRateFetching,
    setCalculatorRounding,
    addTransactionInformation,
    dispatch,
  } = useCalculatorRoundingStore();
  const { setLoading } = useCommonStore();
  const { resetStep2ToFirstVisit } = useFormAddTransactionStore();

  const fetchLookupFee = async ({
    amount,
    getAmount,
    dmCode,
    information,
    isOnlyCurrency,
    countryCode,
    pickupOfficeId,
    isReceiverField,
  }) => {
    if (isEmpty(countryCode) || isEmpty(dmCode)) return;

    const { currentCurrencySelected, currentRate } = information || {};

    const payload = {
      amount: amount || 0,
      getAmount: getAmount || 0,
      countryCode: countryCode || '',
      currencyCode: isOnlyCurrency
        ? currentCurrencySelected?.currencyCode
        : 'AUD',
      dmCode: dmCode || '',
      pickupOfficeId: pickupOfficeId || '',
      rate: currentRate || 0,
    };

    try {
      const remitPayload = {
        ...(isReceiverField
          ? {
              receivingAmount: payload['getAmount'],
            }
          : {
              sendingAmount: payload['amount'],
            }),
        rate: payload['rate'],
        currencyCode: payload['currencyCode'],
        dmCode: payload['dmCode'],
        countryCode: payload['countryCode'],
        pickupOfficeId: payload['pickupOfficeId'],
      };

      // Skip the /quote call when the request is identical to the one behind the
      // stored response. Both the signature and the reuse rule now live in
      // `quoteLifecycle` — this hook asks rather than deciding, so no second
      // place in the app decides whether a stored quote may be reused. The
      // signature is still persisted on the response object below so it shares
      // the response lifecycle (gone when the response is cleared).
      const quoteSignature = buildQuoteSignature(remitPayload);

      if (isStoredQuoteReusable(calculatorRoundingResponse, quoteSignature)) {
        const { fee, remittance } = calculatorRoundingResponse;
        return {
          ...information,
          [ADD_TRANSACTION_YOU_SEND_NAME]: remittance?.sendingAmount || 0,
          [ADD_TRANSACTION_RECEIVER_GET_NAME]: remittance?.receivingAmount || 0,
          feeAmount: fee,
        };
      }

      // remitRoundingRequest returns null on a network/system failure; guard the
      // destructure so an offline error does not throw here (which would bubble
      // up and, with no error boundary, blank the page). No data -> fall through
      // and return undefined; fetchCurrentFee keeps the existing form state.
      const { data } = (await remitRoundingRequest(remitPayload)) || {};

      if (data) {
        setCalculatorRoundingResponse({
          ...data,
          __quoteSignature: quoteSignature,
        });

        const { fee, remittance, originalFee } = data || {};

        const newInfomation = {
          ...information,
          [ADD_TRANSACTION_YOU_SEND_NAME]: remittance?.sendingAmount || 0,
          [ADD_TRANSACTION_RECEIVER_GET_NAME]: remittance?.receivingAmount || 0,
          feeAmount: fee,
        };
        return newInfomation;
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useCalculatorRounding ❌ fetchLookupFee ➡️ ERROR ↩`,
        error?.message,
      );
      setCalculatorRoundingResponse();
    }
  };
  const lookupFee = async ({
    amount,
    getAmount,
    information,
    countryCode,
    dmCode,
    isReceiverField,
  } = {}) => {
    try {
      const isOnlyCurrency = !information?.isAddAnotherReceivingCurrency;

      const newInformation = await fetchLookupFee({
        amount,
        getAmount,
        dmCode,
        information,
        isOnlyCurrency,
        countryCode,
        pickupOfficeId: information?.currentCurrencySelected?.pickupOfficeId,
        isReceiverField,
      });

      return newInformation;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useCalculatorRounding ❌ lookupFee ➡️ ERROR ↩`,
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };
  const fetchCurrentFee = async (
    information = null,
    setInformation = FUNC_DEFAULT,
    countryCode = '',
    dmCode = '',
    maxSendLimit = 30000,
    callback = FUNC_DEFAULT,
    isBackup = false,
    isReceiverField = false,
  ) => {
    if (isEmpty(countryCode) || isEmpty(dmCode)) return;

    let newInformation = { ...information };

    const sendAmount = numberUtils.mathRound(
      newInformation[ADD_TRANSACTION_YOU_SEND_NAME] || 0,
    );
    const sendAnotherAmount = numberUtils.mathRound(
      newInformation[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
    );
    const sendTotalAmount = numberUtils.mathRound(
      sendAmount + sendAnotherAmount,
    );

    const getReceiverAmount = numberUtils.mathRound(
      newInformation[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0,
    );
    const getReceiverAnotherAmount = numberUtils.mathRound(
      newInformation[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME] || 0,
    );
    const getReceiverTotalAmount = numberUtils.mathRound(
      getReceiverAmount + getReceiverAnotherAmount,
    );

    if (!sendTotalAmount) {
      newInformation = {
        ...newInformation,
        feeAmount: 0,
      };

      // Amount cleared to empty/0: drop the stored quote (and its dedup
      // signature) so re-entering an amount triggers a fresh /quote instead of
      // reusing the previous response.
      !isBackup && setCalculatorRoundingResponse();

      console.log(
        `⭐ useCalculatorRounding 👉 fetchCurrentFee ➡️ newInformation 1 ↩`,
        newInformation,
      );
      !isBackup && setInformation(newInformation);

      callback && callback(newInformation);

      return;
    }

    newInformation = await lookupFee({
      amount: sendTotalAmount,
      getAmount: getReceiverTotalAmount,
      information: newInformation,
      countryCode,
      dmCode,
      isReceiverField,
    });

    console.log(
      `⭐ useCalculatorRounding 👉 fetchCurrentFee ➡️ newInformation 2 ↩`,
      newInformation,
    );
    // A failed fee lookup (e.g. offline/system error) yields no information.
    // Never push undefined into the form state: the next render would deref
    // `information[...]` on undefined and, with no error boundary, blank the
    // whole page. Bail instead — the system-error modal is surfaced from the
    // API layer (checkSystemError), and the last valid amounts stay on screen.
    if (!newInformation) return;

    !isBackup && setInformation(newInformation);

    callback && callback(newInformation);
  };

  const fetchRate = async ({
    countryCode,
    currency,
    setRate,
    rateProp,
    currencyProp,
    information,
    setInformation,
    callback,
  }) => {
    setRateFetching(true);

    try {
      const payload = {
        countryCode: currency?.countryCode || countryCode,
        currencyCode: currency?.currencyCode,
        dmCode: currency?.dmCode,
      };

      const { data } = await api.lookupRate(
        payload.countryCode,
        payload.dmCode,
        payload.currencyCode,
      );

      if (data) {
        setRate(data.rateValue);

        if (information && setInformation) {
          const newInformation = {
            ...information,
            ...(rateProp && { [rateProp]: data }),
            ...(currencyProp && { [currencyProp]: currency }),
            currentRate: data.rateValue,
          };
          setInformation(newInformation);
        }

        // Dispatch only the remittance change, not a full snapshot of
        // addTransactionInformation. Spreading the render-time snapshot here
        // clobbered the sibling top-level discount keys (discountFetchKey /
        // discountDeriveKey / discountsActive / paymentMethod) that the discount
        // recovery writes, reverting them and hiding the discount section on a
        // currency change. The reducer shallow-merges, so omitting the spread
        // preserves those current top-level values.
        const payloadStore = {
          remittance: {
            ...addTransactionInformation.remittance,
            ...(rateProp && { [rateProp]: data }),
            ...(currencyProp && { [currencyProp]: currency }),
          },
        };
        dispatch({
          type: SET_ADD_TRANSACTION_INFORMATION,
          payload: payloadStore,
        });

        callback && callback();
      }
    } catch (error) {
      checkMaintenance(error);
      // On Step 2, closing the system-error modal resets Step 2 to first-visit.
      checkSystemError(error, resetStep2ToFirstVisit);
      console.error(
        `🔥 useCalculatorRounding ❌ fetchRate ➡️ ERROR ↩`,
        error?.message,
      );
    }
  };

  return { fetchCurrentFee, fetchRate };
};

export default useCalculatorRounding;
